import { Injectable } from '@angular/core';
import { AccountService } from './account.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private accountService: AccountService) {}
  private permissions: any = {};

  // Raccourcis
  hasAdministrateurPermission = (): boolean => this.hasPermission(EnumPermissions.administrateurPermissionCode);
  hasRealiseLecturePermission = (): boolean => this.hasPermission(EnumPermissions.realiseLecturePermissionCode);
  hasRealiseEcriturePermission = (): boolean => this.hasPermission(EnumPermissions.realiseEcriturePermissionCode);
  hasPrevisionLecturePermission = (): boolean => this.hasPermission(EnumPermissions.previsionLecturePermissionCode);
  hasPrevisionEcriturePermission = (): boolean => this.hasPermission(EnumPermissions.previsionEcriturePermissionCode);
  hasRealiseTransmissionPermission = (): boolean => this.hasPermission(EnumPermissions.realiseTransmissionPermissionCode);
  hasPrevisionTransmissionPermission = (): boolean => this.hasPermission(EnumPermissions.previsionTransmissionPermissionCode);
  hasUtilisateursRolesPermission = (): boolean => this.hasPermission(EnumPermissions.utilisateursRolesPermissionCode);
  hasUtilisateursIndicateursPermission = (): boolean => this.hasPermission(EnumPermissions.utilisateursIndicateursPermissionCode);
  hasPrevisionEditionApresTransmissionPermission = (): boolean => this.hasPermission(EnumPermissions.previsionEditionApresTransmissionPermissionCode);
  hasPrevisionValidationPermission = (): boolean => this.hasPermission(EnumPermissions.previsionValidationPermissionCode);
  hasTableauIndicateurPermission = (): boolean => this.hasPermission(EnumPermissions.tableauIndicateurPermissionCode);
  hasTableauIndicateurEtablissementsPermission = (): boolean => this.hasPermission(EnumPermissions.tableauIndicateurEtablissementsPermissionCode);
  hasTableauIndicateurFormationPermission = (): boolean => this.hasPermission(EnumPermissions.tableauIndicateurFormationPermissionCode);
  hasIndicateur15Permission = (): boolean => this.hasPermission(EnumPermissions.indicateur15PermissionCode);
  hasMultisitesPermission = (): boolean => this.hasPermission(EnumPermissions.multisitesPermissionCode);
  hasExportFormationsExcelPermission = (): boolean => this.hasPermission(EnumPermissions.exportFormationsExcelPermissionCode);
  hasExportDgPermission = (): boolean => this.hasPermission(EnumPermissions.exportDgPermissionCode);
  hasAlerteMailPermission = (): boolean => this.hasPermission(EnumPermissions.alerteMailPermissionCode);
  hasRealiseEditionApresTransmissionPermission = (): boolean => this.hasPermission(EnumPermissions.realiseEditionApresTransmissionCode);
  hasGestionMetasPermission = (): boolean => this.hasPermission(EnumPermissions.gestionMetasPermissionCode);
  hasGestionObjectifsPermission = (): boolean => this.hasPermission(EnumPermissions.gestionObjectifsPermissionCode);
  hasGestionDiplomesPermission = (): boolean => this.hasPermission(EnumPermissions.gestionDiplomesPermissionCode);
  hasGestionEtablissementsPermission = (): boolean => this.hasPermission(EnumPermissions.gestionEtablissementsPermissionCode);
  hasExportIndicateur15BisPermission = (): boolean => this.hasPermission(EnumPermissions.exportIndicateur15BisPermissionCode);

  hasUtilisateursPermission(): boolean {
    return this.hasPermission(EnumPermissions.utilisateursPermissionCode) || this.hasPermission(EnumPermissions.utilisateursRolesPermissionCode) || this.hasPermission(EnumPermissions.utilisateursIndicateursPermissionCode);
  }
  hasPrevisionPermission(indicateurId: number): boolean {
    return this.accountService.getUtilisateurAngular()?.AccesPrevisions?.some(p => p == indicateurId);
  }
  hasRealisePermission(indicateurId: number): boolean {
    return this.accountService.getUtilisateurAngular()?.AccesRealises?.some(p => p == indicateurId);
  }

  hasPrevisionEditPermission(indicateurId: number): boolean {
    return this.accountService.getUtilisateurAngular()?.AccesPrevisions?.some(p => p == indicateurId) && this.hasPrevisionEcriturePermission();
  }
  hasRealiseEditPermission(indicateurId: number): boolean {
    return this.accountService.getUtilisateurAngular()?.AccesRealises?.some(p => p == indicateurId) && this.hasRealiseEcriturePermission();
  }

  public hasPermission(permissionCode: string): boolean {
    if (this.permissions[permissionCode] == null) {
      this.permissions[permissionCode] = this.accountService.getUtilisateurAngular()?.Permissions?.some(p => p == permissionCode);
    }
    return this.permissions[permissionCode];
  }

  get canViewIndicateur(): boolean {
    return this.canViewPrevision() || this.canViewRealise();
  }
  get canViewSaisieDonnees() {
    return this.hasAdministrateurPermission() || this.hasPrevisionLecturePermission() || this.hasPrevisionEcriturePermission() || this.hasIndicateur15Permission();
  }
  canViewPrevision(indicateurId?: number): boolean {
    return (
      this.hasAdministrateurPermission() ||
      (this.hasPrevisionEcriturePermission() && !indicateurId) || // || this.hasPrevisionLecturePermission() sinon une ecole voit toutes les saisies
      (this.hasPrevisionEcriturePermission() && indicateurId && this.hasPrevisionEditPermission(indicateurId)) ||
      (this.hasRealiseLecturePermission() && indicateurId && this.hasPrevisionPermission(indicateurId))
    );
  }
  canViewRealise(indicateurId?: number): boolean {
    return (
      this.hasAdministrateurPermission() ||
      (this.hasRealiseEcriturePermission() && !indicateurId) || // || this.hasRealiseLecturePermission()
      (this.hasRealiseEcriturePermission() && indicateurId && this.hasRealiseEditPermission(indicateurId)) ||
      (this.hasRealiseLecturePermission() && indicateurId && this.hasRealisePermission(indicateurId))
    );
  }
  get canViewTransmission(): boolean {
    return this.canViewPrevisionTransmission || this.canViewRealiseTransmission;
  }
  get canViewPrevisionTransmission(): boolean {
    return this.hasAdministrateurPermission() || this.hasPrevisionTransmissionPermission();
  }
  get canViewRealiseTransmission(): boolean {
    return this.hasAdministrateurPermission() || this.hasRealiseTransmissionPermission();
  }
  get canViewPrevisionOrRealiseTransmission(): boolean {
    return this.canViewRealiseTransmission || this.canViewPrevisionTransmission;
  }
  get canViewUtilisateurs(): boolean {
    return this.hasAdministrateurPermission() || this.hasUtilisateursPermission();
  }
  get canViewRoles(): boolean {
    return this.hasAdministrateurPermission() || this.hasUtilisateursRolesPermission();
  }
  get canViewEditMeta(): boolean {
    return this.hasGestionMetasPermission();
  }
  get canViewEditObjectif(): boolean {
    return this.hasGestionObjectifsPermission();
  }
  get canViewEditDiplome(): boolean {
    return this.hasGestionDiplomesPermission();
  }
  get canViewGestionEtablissements(): boolean {
    return this.hasGestionEtablissementsPermission();
  }
  get canViewAdministrationParametre(): boolean {
    return this.hasAdministrateurPermission();
  }
  get canViewEtablissementEmail(): boolean {
    return this.hasAdministrateurPermission();
  }
  get canViewActualites(): boolean {
    return this.hasAdministrateurPermission();
  }

  userHasRealiseEditPermission = this.hasRealiseEditPermission;
  userHasPrevisionEditPermission = this.hasPrevisionEditPermission;

  get canViewAdministration(): boolean {
    return this.canViewEditMeta || this.canViewUtilisateurs;
  }

  canViewIndicateur11() {
    return this.canViewRealise(1) || this.canViewRealise(2) || this.canViewRealise(3) || this.canViewRealise(4) || this.canViewPrevision(1) || this.canViewPrevision(2) || this.canViewPrevision(3) || this.canViewPrevision(4);
  }
  canViewIndicateur12() {
    return this.canViewRealise(5) || this.canViewRealise(6) || this.canViewPrevision(5) || this.canViewPrevision(6);
  }
  canViewIndicateur21() {
    return this.canViewRealise(7) || this.canViewPrevision(7);
  }
  canViewIndicateur22() {
    return this.canViewRealise(8) || this.canViewRealise(9) || this.canViewPrevision(8) || this.canViewPrevision(9);
  }
  canViewIndicateur23() {
    return this.canViewRealise(10) || this.canViewPrevision(10);
  }
  canViewIndicateur31() {
    return this.canViewRealise(11) || this.canViewPrevision(11);
  }
  canViewIndicateur41() {
    return this.canViewRealise(12) || this.canViewPrevision(12);
  }
  canViewIndicateur42() {
    return this.canViewRealise(13) || this.canViewPrevision(13);
  }
  canViewIndicateur43() {
    return this.canViewRealise(14) || this.canViewPrevision(14);
  }
  canViewIndicateur44() {
    return this.hasAdministrateurPermission() || this.hasIndicateur15Permission() || this.canViewRealise(15) || this.canViewPrevision(15);
  }
  get canViewValidationTransmission(): boolean {
    return this.hasAdministrateurPermission() || this.hasPrevisionValidationPermission();
  }
  get canViewTableauIndicateur(): boolean {
    return this.hasAdministrateurPermission() || this.hasTableauIndicateurPermission();
  }
  get canViewTableauEtablissements(): boolean {
    return this.hasAdministrateurPermission() || this.hasTableauIndicateurEtablissementsPermission();
  }
  get canViewTableauFormation(): boolean {
    return this.hasAdministrateurPermission() || this.hasTableauIndicateurFormationPermission() || this.hasExportFormationsExcelPermission();
  }
  get canViewTableau(): boolean {
    return this.canViewTableauIndicateur || this.canViewTableauEtablissements || this.canViewTableauFormation;
  }
  get canViewFormation(): boolean {
    return this.canViewRealise(1) || this.canViewRealise(2) || this.canViewRealise(3);
  }
  canViewExportDg(): boolean {
    return this.hasAdministrateurPermission() || this.hasExportDgPermission();
  }
  get canViewAlerteMailPermission(): boolean {
    return this.hasAdministrateurPermission() || this.hasAlerteMailPermission();
  }

  get canViewFakePermission(): boolean {
    return false;
  }
}

export enum EnumPermissions {
  administrateurPermissionCode = 'administrateur',
  realiseLecturePermissionCode = 'realise.lecture',
  realiseEcriturePermissionCode = 'realise.ecriture',
  previsionLecturePermissionCode = 'prevision.lecture',
  previsionEcriturePermissionCode = 'prevision.ecriture',
  realiseTransmissionPermissionCode = 'realise.transmission',
  previsionTransmissionPermissionCode = 'prevision.transmission',
  utilisateursPermissionCode = 'utilisateurs',
  utilisateursRolesPermissionCode = 'utilisateurs.roles',
  utilisateursIndicateursPermissionCode = 'utilisateurs.indicateurs',
  previsionEditionApresTransmissionPermissionCode = 'prevision.edition.apres.transmission',
  previsionValidationPermissionCode = 'prevision.validation',
  tableauIndicateurPermissionCode = 'tableau.indicateur',
  tableauIndicateurEtablissementsPermissionCode = 'tableau.etablissements',
  tableauIndicateurFormationPermissionCode = 'tableau.formation',
  indicateur15PermissionCode = 'indicateur.15',
  multisitesPermissionCode = 'multisites',
  exportFormationsExcelPermissionCode = 'export.formations.excel',
  exportDgPermissionCode = 'export.dg',
  alerteMailPermissionCode = 'alerte.mail',
  realiseEditionApresTransmissionCode = 'realise.edition.apres.transmission',
  gestionMetasPermissionCode = 'gestion-metas',
  gestionObjectifsPermissionCode = 'gestion-objectifs',
  gestionDiplomesPermissionCode = 'gestion-diplomes',
  gestionEtablissementsPermissionCode = 'gestion-etablissements',
  exportIndicateur15BisPermissionCode = 'export-indicateur-15-bis',
}
